// ═══════════════════════════════════════════════════════════════
// Handoff — Event/Incident Detail
// Positions, handoff notes, AI briefing, action items
// Uses shared incidents from authStore
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Text, Paper, Group, Stack, Badge, Button, Tabs, SimpleGrid,
  Modal, TextInput, Select, Textarea, Loader, Center, Divider,
} from '@mantine/core';
import {
  IconArrowLeft, IconPlus, IconSparkles, IconSun, IconMoon, IconExternalLink,
} from '@tabler/icons-react';
import { useHandoffStore } from '../../store/handoffStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { ShiftType, HandoffSection } from '../../types';
import { DEFAULT_TEMPLATE_SECTIONS, PRIORITY_META } from '../../types';
import { primary, semantic } from '../../lib/colors';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

function detectShift(): ShiftType {
  const hour = new Date().getHours();
  return hour >= 6 && hour < 18 ? 'day' : 'night';
}

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canEdit } = useModulePermission('handoff');
  const { incidents, loadIncidents } = useAuthStore();
  const {
    notes, actionItems, rosterUsers, isLoading,
    loadNotes, loadPositions, loadActionItems, loadRosterUsers,
    createNote, createActionItem, completeActionItem,
    generateBriefing, generatePriorities,
  } = useHandoffStore();
  const { user } = useAuthStore();

  const [tab, setTab] = useState<string | null>('overview');
  const [noteModal, setNoteModal] = useState(false);
  const [selPosition, setSelPosition] = useState('');
  const [noteSections, setNoteSections] = useState<HandoffSection[]>(
    DEFAULT_TEMPLATE_SECTIONS.map(s => ({ title: s.title, content: '' }))
  );
  const [actionModal, setActionModal] = useState(false);
  const [actionTitle, setActionTitle] = useState('');
  const [actionDesc, setActionDesc] = useState('');
  const [actionPriority, setActionPriority] = useState('normal');
  const [actionAssignee, setActionAssignee] = useState('');
  const [actionDeadline, setActionDeadline] = useState('');
  const [aiLoading, setAiLoading] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [aiBriefing, setAiBriefing] = useState('');
  const [aiPrioritiesText, setAiPrioritiesText] = useState('');

  useEffect(() => {
    loadIncidents();
    loadNotes();
    loadPositions();
    loadActionItems();
    loadRosterUsers();
  }, []);

  // Find the incident from the shared incidents table
  const incident = incidents.find(e => String(e.id) === id);
  const eventNotes = notes.filter(n => String(n.eventId) === id)
    .sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? ''));
  const { getPositionsForEvent } = useHandoffStore();
  const eventPositions = getPositionsForEvent(id)
    .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
  const eventActions = actionItems.filter(a => String(a.eventId) === id);

  if (isLoading && !incident) return <Center p="xl"><Loader /></Center>;
  if (!incident) return (
    <Paper p="xl" radius="sm" withBorder>
      <Text ta="center" c="dimmed">Incident not found.</Text>
      <Center mt="md">
        <Button variant="subtle" leftSection={<IconArrowLeft size={14} />} onClick={() => navigate('/handoff/events')}>
          Back to Incidents
        </Button>
      </Center>
    </Paper>
  );

  const drafts = eventNotes.filter(n => n.status === 'draft');
  const submitted = eventNotes.filter(n => n.status === 'submitted');
  const acknowledged = eventNotes.filter(n => n.status === 'acknowledged');

  const handleCreateNote = async () => {
    if (!selPosition) return;
    setSaving(true);
    const pos = eventPositions.find(p => String(p.id) === selPosition);
    const shift = detectShift();
    const rid = await createNote({
      eventId: String(incident.id),
      positionId: selPosition,
      positionName: pos?.positionName ?? '',
      fromShift: shift,
      toShift: shift === 'day' ? 'night' : 'day',
      shiftDate: new Date().toISOString().split('T')[0],
      sections: noteSections,
      senderName: user?.fullName ?? '',
    });
    setNoteModal(false);
    setNoteSections(DEFAULT_TEMPLATE_SECTIONS.map(s => ({ title: s.title, content: '' })));
    navigate(`/handoff/note/${rid}`);
    setSaving(false);
  };

  const handleCreateAction = async () => {
    if (!actionTitle.trim()) return;
    setSaving(true);
    await createActionItem({
      eventId: String(incident.id),
      title: actionTitle.trim(),
      description: actionDesc.trim(),
      priority: actionPriority as 'low' | 'normal' | 'high' | 'critical',
      assignedToName: actionAssignee,
      deadline: actionDeadline,
    });
    setActionModal(false);
    setActionTitle(''); setActionDesc(''); setActionPriority('normal'); setActionAssignee(''); setActionDeadline('');
    setSaving(false);
  };

  // AI handlers use a HandoffEvent-compatible object from incident data
  const eventForAI = {
    id: incident.id,
    name: incident.name,
    eventType: incident.incidentType,
    status: incident.status,
    description: incident.description,
    aiBriefing: '',
    aiPriorities: '',
    creatorName: incident.createdByName,
    creatorEmail: '',
    datecreated: incident.datecreated ?? '',
  };

  const handleGenBriefing = async () => {
    setAiLoading('briefing');
    try {
      const result = await generateBriefing(eventForAI, eventNotes);
      setAiBriefing(result);
    } finally { setAiLoading(null); }
  };

  const handleGenPriorities = async () => {
    setAiLoading('priorities');
    try {
      const result = await generatePriorities(eventForAI, eventNotes, eventActions);
      setAiPrioritiesText(result);
    } finally { setAiLoading(null); }
  };

  const statusColor = (s: string) => s === 'active' ? 'green' : s === 'closed' ? 'gray' : 'yellow';
  const noteStatusColor = (s: string) => s === 'draft' ? 'gray' : s === 'submitted' ? 'yellow' : 'green';
  const ShiftIcon = ({ shift }: { shift: string }) => shift === 'day' ? <IconSun size={12} /> : <IconMoon size={12} />;

  return (
    <div>
      <Breadcrumbs items={[
        { label: 'Hub', to: '/' },
        { label: 'Handoff', to: '/handoff' },
        { label: 'Events', to: '/handoff/events' },
        { label: incident.name || 'Event' },
      ]} />
      <Group gap="xs" mb="md">
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/handoff/events')}>Incidents</Button>
        <Text c="dimmed" size="sm">/</Text>
        <Text fw={600} size="sm">{incident.name}</Text>
        <Badge size="xs" color={statusColor(incident.status)} tt="capitalize">{incident.status}</Badge>
        <Badge size="xs" variant="outline" color="gray" tt="capitalize">{incident.incidentType}</Badge>
      </Group>

      {incident.description && (
        <Text size="sm" c="dimmed" mb="md">{incident.description}</Text>
      )}

      <Tabs value={tab} onChange={setTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          <Tabs.Tab value="positions">Positions ({eventPositions.length})</Tabs.Tab>
          <Tabs.Tab value="handoffs">Handoffs ({eventNotes.length})</Tabs.Tab>
          <Tabs.Tab value="actions">Actions ({eventActions.length})</Tabs.Tab>
          <Tabs.Tab value="ai">AI Briefing</Tabs.Tab>
        </Tabs.List>

        {/* Overview Tab */}
        <Tabs.Panel value="overview">
          <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
            <Paper p="md" radius="sm" withBorder ta="center">
              <Text fz={24} fw={700}>{eventPositions.length}</Text>
              <Text size="xs" c="dimmed">Positions</Text>
            </Paper>
            <Paper p="md" radius="sm" withBorder ta="center">
              <Text fz={24} fw={700}>{eventNotes.length}</Text>
              <Text size="xs" c="dimmed">Handoffs</Text>
            </Paper>
            <Paper p="md" radius="sm" withBorder ta="center">
              <Text fz={24} fw={700} c={semantic.warning}>{submitted.length}</Text>
              <Text size="xs" c="dimmed">Pending Ack</Text>
            </Paper>
            <Paper p="md" radius="sm" withBorder ta="center">
              <Text fz={24} fw={700} c={semantic.info}>{eventActions.filter(a => a.status === 'pending').length}</Text>
              <Text size="xs" c="dimmed">Open Actions</Text>
            </Paper>
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Paper p="md" radius="sm" withBorder>
              <Text fw={600} size="sm" mb="xs">Incident Details</Text>
              <Stack gap="xs">
                <div><Text size="xs" c="dimmed">Type</Text><Text size="sm" tt="capitalize">{incident.incidentType}</Text></div>
                {incident.location && <div><Text size="xs" c="dimmed">Location</Text><Text size="sm">{incident.location}</Text></div>}
                {incident.commander && <div><Text size="xs" c="dimmed">Commander</Text><Text size="sm">{incident.commander}</Text></div>}
                {incident.description && <div><Text size="xs" c="dimmed">Description</Text><Text size="sm">{incident.description}</Text></div>}
                <div><Text size="xs" c="dimmed">Created By</Text><Text size="sm">{incident.createdByName}</Text></div>
              </Stack>
            </Paper>
            <Paper p="md" radius="sm" withBorder>
              <Group justify="space-between" mb="xs">
                <Text fw={600} size="sm">Recent Handoffs</Text>
                {canEdit && <Button variant="subtle" size="xs" leftSection={<IconPlus size={12} />} onClick={() => setNoteModal(true)}>New</Button>}
              </Group>
              {eventNotes.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="sm">No handoffs yet</Text>
              ) : (
                <Stack gap="xs">
                  {eventNotes.slice(0, 4).map(n => (
                    <Group key={n.id} justify="space-between" style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/handoff/note/${n.id}`)}>
                      <Group gap="xs">
                        <ShiftIcon shift={n.fromShift} />
                        <Text size="sm">{n.positionName}</Text>
                      </Group>
                      <Badge size="xs" color={noteStatusColor(n.status)} tt="capitalize">{n.status}</Badge>
                    </Group>
                  ))}
                </Stack>
              )}
            </Paper>
          </SimpleGrid>
        </Tabs.Panel>

        {/* Positions Tab — read-only summary, manage in Staff Roster */}
        <Tabs.Panel value="positions">
          <Group justify="flex-end" mb="sm">
            <Button size="xs" variant="light" leftSection={<IconExternalLink size={14} />}
              onClick={() => navigate('/handoff/staff')}>
              Manage in Staff Roster
            </Button>
          </Group>
          {eventPositions.length === 0 ? (
            <Paper p="xl" radius="sm" withBorder>
              <Text ta="center" c="dimmed">
                No positions set up yet.{' '}
                <Text span c={primary[700]} style={{ cursor: 'pointer' }} onClick={() => navigate('/handoff/staff')}>
                  Go to Staff Roster
                </Text>{' '}
                to create and assign positions.
              </Text>
            </Paper>
          ) : (
            <Stack gap="sm">
              {eventPositions.map(p => (
                <Paper key={p.id} p="md" radius="sm" withBorder>
                  <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                      <Text fw={600}>{p.positionName}</Text>
                      {p.shortName && <Badge size="xs" variant="outline" color="gray">{p.shortName}</Badge>}
                    </Group>
                    <Badge size="xs" color={
                      (p.dayUserName && p.nightUserName) ? 'green' :
                      (p.dayUserName || p.nightUserName) ? 'yellow' : 'red'
                    }>
                      {(p.dayUserName && p.nightUserName) ? 'Fully Staffed' :
                       (p.dayUserName || p.nightUserName) ? 'Partial' : 'Unstaffed'}
                    </Badge>
                  </Group>
                  <SimpleGrid cols={2}>
                    <div>
                      <Group gap={4} mb={2}><IconSun size={12} color={semantic.warning} /><Text size="xs" fw={500}>Day Shift</Text></Group>
                      <Text size="sm">{p.dayUserName || <Text span c="dimmed" size="sm">Unassigned</Text>}</Text>
                    </div>
                    <div>
                      <Group gap={4} mb={2}><IconMoon size={12} color={primary[700]} /><Text size="xs" fw={500}>Night Shift</Text></Group>
                      <Text size="sm">{p.nightUserName || <Text span c="dimmed" size="sm">Unassigned</Text>}</Text>
                    </div>
                  </SimpleGrid>
                </Paper>
              ))}
            </Stack>
          )}
        </Tabs.Panel>

        {/* Handoffs Tab */}
        <Tabs.Panel value="handoffs">
          <Group justify="flex-end" mb="sm">
            {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setNoteModal(true)}>New Handoff</Button>}
          </Group>

          {eventNotes.length === 0 ? (
            <Paper p="xl" radius="sm" withBorder><Text ta="center" c="dimmed">No handoffs yet.</Text></Paper>
          ) : (
            <Stack gap="md">
              {submitted.length > 0 && (
                <div>
                  <Text fw={600} size="sm" mb="xs" c={semantic.warning}>Pending Acknowledgement ({submitted.length})</Text>
                  <Stack gap="xs">
                    {submitted.map(n => (
                      <Paper key={n.id} p="sm" radius="sm" withBorder style={{ borderLeft: '3px solid var(--color-warning)', cursor: 'pointer' }}
                        onClick={() => navigate(`/handoff/note/${n.id}`)}>
                        <Group justify="space-between">
                          <Group gap="xs">
                            <ShiftIcon shift={n.fromShift} />
                            <Text size="sm" fw={500}>{n.positionName}</Text>
                            <Text size="xs" c="dimmed">{n.shiftDate}</Text>
                          </Group>
                          <Text size="xs" c="dimmed">From: {n.senderName}</Text>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </div>
              )}

              {drafts.length > 0 && (
                <div>
                  <Text fw={600} size="sm" mb="xs" c="dimmed">Drafts ({drafts.length})</Text>
                  <Stack gap="xs">
                    {drafts.map(n => (
                      <Paper key={n.id} p="sm" radius="sm" withBorder style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/handoff/note/${n.id}`)}>
                        <Group justify="space-between">
                          <Group gap="xs">
                            <ShiftIcon shift={n.fromShift} />
                            <Text size="sm">{n.positionName}</Text>
                            <Badge size="xs" color="gray">Draft</Badge>
                          </Group>
                          <Text size="xs" c="dimmed">{n.shiftDate}</Text>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </div>
              )}

              {acknowledged.length > 0 && (
                <div>
                  <Text fw={600} size="sm" mb="xs" c={semantic.success}>Acknowledged ({acknowledged.length})</Text>
                  <Stack gap="xs">
                    {acknowledged.map(n => (
                      <Paper key={n.id} p="sm" radius="sm" withBorder style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/handoff/note/${n.id}`)}>
                        <Group justify="space-between">
                          <Group gap="xs">
                            <ShiftIcon shift={n.fromShift} />
                            <Text size="sm">{n.positionName}</Text>
                            <Badge size="xs" color="green">Acknowledged</Badge>
                          </Group>
                          <Text size="xs" c="dimmed">{n.shiftDate}</Text>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </div>
              )}
            </Stack>
          )}
        </Tabs.Panel>

        {/* Actions Tab */}
        <Tabs.Panel value="actions">
          <Group justify="flex-end" mb="sm">
            {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setActionModal(true)}>New Action</Button>}
          </Group>
          {eventActions.length === 0 ? (
            <Paper p="xl" radius="sm" withBorder><Text ta="center" c="dimmed">No action items.</Text></Paper>
          ) : (
            <Stack gap="xs">
              {eventActions.sort((a, b) => {
                if (a.status !== b.status) return a.status === 'pending' ? -1 : 1;
                const pOrder: Record<string, number> = { critical: 0, high: 1, normal: 2, low: 3 };
                return (pOrder[a.priority] ?? 2) - (pOrder[b.priority] ?? 2);
              }).map(a => {
                const isOverdue = a.status === 'pending' && a.deadline && new Date(a.deadline) < new Date();
                const pm = PRIORITY_META[a.priority];
                return (
                  <Paper key={a.id} p="sm" radius="sm" withBorder
                    style={{ borderLeft: `3px solid ${isOverdue ? 'var(--color-error)' : pm.color}`, opacity: a.status === 'completed' ? 0.6 : 1 }}>
                    <Group justify="space-between">
                      <div>
                        <Group gap="xs" mb={2}>
                          <Text size="sm" fw={500} td={a.status === 'completed' ? 'line-through' : undefined}>{a.title}</Text>
                          <Badge size="xs" style={{ background: pm.color + '15', color: pm.color }}>{pm.label}</Badge>
                          {isOverdue && <Badge size="xs" color="red">Overdue</Badge>}
                          {a.status === 'completed' && <Badge size="xs" color="green">Done</Badge>}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {a.assignedToName && `Assigned: ${a.assignedToName}`}
                          {a.deadline && ` — Due: ${new Date(a.deadline).toLocaleDateString()}`}
                        </Text>
                      </div>
                      {a.status === 'pending' && (
                        <Button variant="subtle" size="xs" color="green"
                          onClick={() => completeActionItem(a.id)}>Complete</Button>
                      )}
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Tabs.Panel>

        {/* AI Briefing Tab */}
        <Tabs.Panel value="ai">
          <Stack gap="md">
            <Group gap="xs">
              <Button size="xs" leftSection={<IconSparkles size={14} />}
                onClick={handleGenBriefing} loading={aiLoading === 'briefing'}
                disabled={eventNotes.filter(n => n.status !== 'draft').length === 0}>
                {aiBriefing ? 'Regenerate Briefing' : 'Generate Briefing'}
              </Button>
              <Button size="xs" variant="light" leftSection={<IconSparkles size={14} />}
                onClick={handleGenPriorities} loading={aiLoading === 'priorities'}
                disabled={eventNotes.filter(n => n.status !== 'draft').length === 0}>
                {aiPrioritiesText ? 'Regenerate Priorities' : 'Generate Priorities'}
              </Button>
            </Group>

            {aiBriefing && (
              <Paper p="md" radius="sm" withBorder style={{ borderLeftWidth: 3, borderLeftColor: 'var(--mantine-color-violet-5)' }}>
                <Text fw={600} size="sm" mb="xs">Incident Briefing</Text>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{aiBriefing}</Text>
              </Paper>
            )}

            {aiPrioritiesText && (
              <Paper p="md" radius="sm" withBorder style={{ borderLeftWidth: 3, borderLeftColor: 'var(--mantine-color-orange-5)' }}>
                <Text fw={600} size="sm" mb="xs">Operational Priorities</Text>
                {(() => {
                  try {
                    let cleaned = aiPrioritiesText.trim();
                    if (cleaned.startsWith('```')) cleaned = cleaned.replace(/^```json?\n?/, '').replace(/```$/, '');
                    const priorities = JSON.parse(cleaned) as Array<{ priority: number; action: string; rationale: string; urgency: string }>;
                    return (
                      <Stack gap="xs">
                        {priorities.map(p => (
                          <Group key={p.priority} gap="sm" wrap="nowrap" align="flex-start">
                            <Text fw={700} fz={16} c="blurple" style={{ minWidth: 24 }}>{p.priority}.</Text>
                            <div>
                              <Group gap="xs" mb={2}>
                                <Text size="sm" fw={500}>{p.action}</Text>
                                <Badge size="xs" color={p.urgency === 'immediate' ? 'red' : p.urgency === 'high' ? 'yellow' : 'gray'}>
                                  {p.urgency}
                                </Badge>
                              </Group>
                              <Text size="xs" c="dimmed">{p.rationale}</Text>
                            </div>
                          </Group>
                        ))}
                      </Stack>
                    );
                  } catch {
                    return <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{aiPrioritiesText}</Text>;
                  }
                })()}
              </Paper>
            )}

            {!aiBriefing && !aiPrioritiesText && (
              <Paper p="xl" radius="sm" withBorder>
                <Text ta="center" c="dimmed">
                  {eventNotes.filter(n => n.status !== 'draft').length === 0
                    ? 'Submit at least one handoff note to enable AI features.'
                    : 'Click a button above to generate an AI briefing or priority list based on handoff data.'}
                </Text>
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Create Handoff Note Modal */}
      <Modal opened={noteModal} onClose={() => setNoteModal(false)} title="New Handoff" size="lg">
        <Stack gap="sm">
          <Select label="Position" placeholder="Select position"
            data={eventPositions.map(p => ({ value: String(p.id), label: p.positionName }))}
            value={selPosition} onChange={v => setSelPosition(v ?? '')} required />
          <Divider label="Sections" labelPosition="left" />
          {noteSections.map((s, i) => (
            <Textarea key={i} label={s.title} placeholder={DEFAULT_TEMPLATE_SECTIONS[i]?.placeholder || 'Enter content...'}
              value={s.content} onChange={e => {
                const updated = [...noteSections];
                updated[i] = { ...updated[i], content: e.currentTarget.value };
                setNoteSections(updated);
              }} minRows={2} autosize />
          ))}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setNoteModal(false)}>Cancel</Button>
            <Button onClick={handleCreateNote} loading={saving} disabled={!selPosition}>Create Handoff</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create Action Item Modal */}
      <Modal opened={actionModal} onClose={() => setActionModal(false)} title="New Action Item" size="md">
        <Stack gap="sm">
          <TextInput label="Title" value={actionTitle} onChange={e => setActionTitle(e.currentTarget.value)} required />
          <Textarea label="Description" value={actionDesc} onChange={e => setActionDesc(e.currentTarget.value)} minRows={2} />
          <Group grow>
            <Select label="Priority"
              data={[{ value: 'low', label: 'Low' }, { value: 'normal', label: 'Normal' }, { value: 'high', label: 'High' }, { value: 'critical', label: 'Critical' }]}
              value={actionPriority} onChange={v => setActionPriority(v ?? 'normal')} />
            <Select label="Assignee" placeholder="Select person..."
              data={rosterUsers.map(u => ({ value: u.fullName, label: u.fullName }))}
              value={actionAssignee || null} onChange={v => setActionAssignee(v ?? '')}
              searchable clearable />
          </Group>
          <TextInput label="Deadline" type="date" value={actionDeadline}
            onChange={e => setActionDeadline(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setActionModal(false)}>Cancel</Button>
            <Button onClick={handleCreateAction} loading={saving} disabled={!actionTitle.trim()}>Create</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
